import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DataList,
  LogLineFilters,
  LogLines,
  NO_LOG_FILTER,
  PageHeader,
  StatusPill,
  type DataListColumn,
  type LogFilter,
} from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, useAuth, useToast } from "@ozpanel/ui";

// Four surfaces the admin console reached NONE of: the journal, container app events, the
// database's own tables, and the support services. Between them 20 routes, all implemented.
//
// The app-event feed is the one that was actively costing something: the node was holding 30
// unacknowledged warnings and errors about tenants' containers, and there was no screen in v2 that
// could show a single one of them.

// ───────────────────────────────── logs ─────────────────────────────────

export function LogsPanel() {
  const { client } = useAuth();
  const [unit, setUnit] = React.useState<string | null>(null);
  const [lines, setLines] = React.useState(200);

  const units = useQuery({
    queryKey: ["log-units"],
    queryFn: () =>
      client.apiFetch<{ units: string[] | null; total?: number; log_files?: Record<string, string[]> }>("/logs/units"),
    retry: false,
  });
  // Logs some services write to FILES, not the journal (JuiceFS) — declared by their module.
  const [fileMod, setFileMod] = React.useState<string | null>(null);
  const [match, setMatch] = React.useState("");
  const fileLog = useQuery({
    queryKey: ["log-file", fileMod, lines, match],
    enabled: !!fileMod,
    queryFn: () =>
      client.apiFetch<{ files: { file: string; lines: string[]; total: number; size_bytes: number; error?: string }[] }>(
        `/logs/file/${encodeURIComponent(String(fileMod))}?lines=${lines}${match ? `&match=${encodeURIComponent(match)}` : ""}`,
      ),
    retry: false,
  });
  const log = useQuery({
    queryKey: ["log-unit", unit, lines],
    enabled: !!unit,
    queryFn: () =>
      client.apiFetch<{ lines?: string[] | null; logs?: string; unit?: string }>(
        `/logs/service/${encodeURIComponent(String(unit))}?lines=${lines}`,
      ),
    retry: false,
  });

  // The rows as the API returned them; LogLines (the one log view) takes them apart — the one log
  // line is laid out with ITS time, anything else is shown as it is.
  const rows = log.data?.logs ?? log.data?.lines ?? [];
  const hasRows = typeof rows === "string" ? rows.trim() !== "" : rows.length > 0;
  // One filter per view, cleared when the view changes: a level chosen for the panel's journal
  // means nothing for the next unit's.
  const [unitFilter, setUnitFilter] = React.useState<LogFilter>(NO_LOG_FILTER);
  const [fileFilter, setFileFilter] = React.useState<LogFilter>(NO_LOG_FILTER);
  React.useEffect(() => setUnitFilter(NO_LOG_FILTER), [unit]);
  React.useEffect(() => setFileFilter(NO_LOG_FILTER), [fileMod]);

  return (
    <div>
      <Section title="Services" bodyClassName="p-0">
        <div className="flex flex-wrap gap-1.5 p-4">
          <StateView loading={units.isLoading} error={units.error} errorContext="the service list">
            {(units.data?.units ?? []).map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => setUnit(u)}
                className={`rounded-full px-2.5 py-1 font-mono text-[11.5px] ${
                  unit === u ? "bg-accentsoft text-accent" : "border border-line text-muted hover:text-fg"
                }`}
              >
                {u}
              </button>
            ))}
          </StateView>
        </div>
      </Section>

      {Object.keys(units.data?.log_files ?? {}).length > 0 && (
        <Section className="mt-6" title="Log files" bodyClassName="p-0">
          <div className="flex flex-wrap gap-1.5 p-4">
            {Object.entries(units.data?.log_files ?? {}).map(([mod, files]) => (
              <button
                key={mod}
                type="button"
                title={files.join(", ")}
                onClick={() => setFileMod(mod)}
                className={`rounded-full px-2.5 py-1 font-mono text-[11.5px] ${
                  fileMod === mod ? "bg-accentsoft text-accent" : "border border-line text-muted hover:text-fg"
                }`}
              >
                {mod}
              </button>
            ))}
          </div>
        </Section>
      )}

      {fileMod && (
        <Section
          className="mt-6"
          title={`${fileMod} — log file on this node`}
          actions={
            <div className="flex items-center gap-2">
              <input
                value={match}
                onChange={(e) => setMatch(e.target.value)}
                placeholder="only lines containing…"
                className="w-48 rounded-lg border border-line2 bg-bg px-2 py-1 text-[12px] text-fg"
              />
              <LogLineFilters
                rows={(fileLog.data?.files ?? []).flatMap((f) => f.lines)}
                value={fileFilter}
                onChange={setFileFilter}
              />
              <select
                value={lines}
                onChange={(e) => setLines(Number(e.target.value))}
                className="rounded-lg border border-line2 bg-bg px-2 py-1 text-[12px] text-fg"
              >
                {[50, 200, 1000].map((n) => (
                  <option key={n} value={n}>{n} lines</option>
                ))}
              </select>
              <Button size="sm" variant="ghost" onClick={() => void fileLog.refetch()}>Refresh</Button>
              <Button size="sm" variant="ghost" onClick={() => setFileMod(null)}>Close</Button>
            </div>
          }
        >
          <StateView loading={fileLog.isLoading} error={fileLog.error} errorContext="this log file">
            {(fileLog.data?.files ?? []).map((f) => (
              <div key={f.file} className="space-y-1">
                <div className="font-mono text-[11.5px] text-faint">
                  {f.file} · {f.total} line(s){f.error ? ` · ${f.error}` : ""}
                </div>
                <LogLines rows={f.lines} filter={fileFilter} empty="No matching lines." />
              </div>
            ))}
          </StateView>
        </Section>
      )}

      {unit && (
        <Section
          className="mt-6"
          title={unit}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <LogLineFilters rows={rows} value={unitFilter} onChange={setUnitFilter} />
              <select
                value={lines}
                onChange={(e) => setLines(Number(e.target.value))}
                className="rounded-lg border border-line2 bg-bg px-2 py-1 text-[12px] text-fg"
              >
                {[50, 200, 1000].map((n) => (
                  <option key={n} value={n}>{n} lines</option>
                ))}
              </select>
              <Button size="sm" variant="ghost" onClick={() => void log.refetch()}>Refresh</Button>
              <Button size="sm" variant="ghost" onClick={() => setUnit(null)}>Close</Button>
            </div>
          }
        >
          <StateView loading={log.isLoading} error={log.error} errorContext="this service's log">
            {hasRows ? (
              <LogLines rows={rows} filter={unitFilter} />
            ) : (
              <div className="text-[13px] text-muted">Nothing logged in the last {lines} lines.</div>
            )}
          </StateView>
        </Section>
      )}
    </div>
  );
}

// ────────────────────────── container app events ──────────────────────────

interface AppEvent {
  id: number;
  app_name?: string;
  domain?: string;
  node?: string;
  kind?: string;
  severity?: string;
  detail?: string;
  at?: string;
  ago?: string;
  // The server records WHO acknowledged it and WHEN, not a boolean.
  acked_at?: string;
  // The acknowledging USER ID, not a name.
  acked_by?: number;
}

export function AppEventsScreen() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [openOnly, setOpenOnly] = React.useState(true);
  // DELETE /app-events: acknowledged events by default, every event with all=true (Oz, 2026-10-25).
  const [clearMode, setClearMode] = React.useState<"acked" | "all" | null>(null);
  const [clearing, setClearing] = React.useState(false);
  const runClear = () => {
    if (!clearMode) return;
    setClearing(true);
    const req =
      clearMode === "all"
        ? client.apiFetch<{ deleted: number; more?: boolean }>("/app-events?all=true", { method: "DELETE" })
        : client.apiFetch<{ deleted: number; more?: boolean }>("/app-events", { method: "DELETE" });
    void req
      .then((d) => {
        notify({ kind: "success", title: `Deleted ${d.deleted} event${d.deleted === 1 ? "" : "s"}`, body: d.more ? "More remain; clear again." : undefined });
        void qc.invalidateQueries({ queryKey: ["app-events"] });
        setClearMode(null);
      })
      .catch((err: unknown) => notify({ kind: "error", title: "Could not delete the events", body: err instanceof Error ? err.message : String(err) }))
      .finally(() => setClearing(false));
  };

  // Two separate queries rather than one filtered client-side: `open=1` is what the server means
  // by unacknowledged, and re-deriving it here would be a second definition that can disagree.
  const q = useQuery({
    queryKey: ["app-events", openOnly],
    queryFn: () =>
      openOnly
        ? client.apiFetch<{ events: AppEvent[] | null; open_alerts?: number; worst?: string; this_node?: string; note?: string }>("/app-events?open=1")
        : client.apiFetch<{ events: AppEvent[] | null; open_alerts?: number; worst?: string; this_node?: string; note?: string }>("/app-events"),
    retry: false,
    refetchInterval: 30000,
  });

  const tone = (s?: string) => (s === "error" || s === "crit" ? "bad" : s === "warn" ? "warn" : "neutral");

  const cols: DataListColumn<AppEvent>[] = [
    {
      key: "severity", label: "", sortable: true, value: (e) => e.severity || "",
      render: (e) => <StatusPill tone={tone(e.severity)}>{e.severity || "info"}</StatusPill>,
    },
    {
      key: "app", label: "App", sortable: true, value: (e) => e.app_name || "",
      render: (e) => (
        <span>
          <span className="font-mono text-[12.5px]">{e.app_name}</span>
          {e.domain && <span className="ml-2 text-[11.5px] text-muted">{e.domain}</span>}
        </span>
      ),
    },
    { key: "node", label: "Where", sortable: true, render: (e) => <span className="text-[12px]">{e.node || "—"}</span> },
    { key: "detail", label: "What happened", render: (e) => <span className="text-[12.5px]">{e.detail || e.kind}</span> },
    { key: "ago", label: "When", sortable: true, value: (e) => e.at || "", render: (e) => <span className="text-[12px] text-muted">{e.ago || e.at}</span> },
  ];

  return (
    <div>
      <PageHeader
        title="Container events"
        subtitle="What happened to tenants' apps, on every node"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpenOnly(!openOnly)}>
              {openOnly ? "Show everything" : "Only unacknowledged"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setClearMode("acked")}>
              Clear acknowledged
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setClearMode("all")}>
              Clear all
            </Button>
          </div>
        }
      />
      <ConfirmDialog
        open={clearMode != null}
        onOpenChange={(o) => {
          if (!o) setClearMode(null);
        }}
        title={clearMode === "all" ? "Delete every container event?" : "Delete the acknowledged container events?"}
        body={
          clearMode === "all"
            ? "Every recorded event on every node is deleted, unacknowledged ones included. The apps are not touched; a transition that happens again is recorded again. This cannot be undone."
            : "The events already acknowledged are deleted; unacknowledged ones stay. The apps are not touched. This cannot be undone."
        }
        confirmLabel="Delete"
        destructive
        pending={clearing}
        onConfirm={runClear}
      />
      <Section
        title={openOnly ? "Unacknowledged" : "All events"}
        hint={q.data?.open_alerts != null ? `${q.data.open_alerts} open` : undefined}
        bodyClassName="p-0"
      >
        {q.data?.note && <div className="border-b border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">{q.data.note}</div>}
        <div className="p-4">
          <DataList
            items={q.data?.events ?? []}
            columns={cols}
            getRowKey={(e) => String(e.id)}
            loading={q.isLoading}
            error={q.error}
            errorContext="the container events"
            emptyMessage={openOnly ? "Nothing outstanding." : "No events recorded."}
            rowActions={(e) =>
              e.severity === "info" ? null : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    void client
                      .apiFetch(`/app-events/${encodeURIComponent(String(e.id))}/ack`, { method: "POST" })
                      .then(() => {
                        notify({ kind: "success", title: "Acknowledged" });
                        void qc.invalidateQueries({ queryKey: ["app-events"] });
                      })
                      .catch((err: unknown) =>
                        notify({ kind: "error", title: "Could not acknowledge", body: err instanceof Error ? err.message : String(err) }),
                      )
                  }
                >
                  Acknowledge
                </Button>
              )
            }
          />
        </div>
      </Section>
      {/* Acknowledging is not fixing, and the difference is worth stating where the button is. */}
      <div className="mt-2 max-w-3xl text-[11.5px] leading-relaxed text-muted">
        Acknowledging clears an event from this list. It does not change the app — if a container is
        crash-looping, the count stops growing here and the container keeps restarting.
      </div>
    </div>
  );
}

// ──────────────────────────── database tables ────────────────────────────

// rows is null when the count could not be read (rows_error says why) — never shown as 0.
interface TableRow { name: string; rows: number | null; rows_error?: string; size_kb: number }

export function DatabaseScreen() {
  const { client } = useAuth();
  const [open, setOpen] = React.useState<string | null>(null);

  const tables = useQuery({
    queryKey: ["db-tables"],
    queryFn: () =>
      client.apiFetch<{ tables: TableRow[] | null; database?: string; total_rows?: number | null; total_size_kb?: number; total_tables?: number; note?: string }>(
        "/database/tables",
      ),
    retry: false,
  });
  const drift = useQuery({
    queryKey: ["db-drift"],
    queryFn: () =>
      client.apiFetch<{ empty_tables?: string[] | null; migrations_applied?: number; tables?: number; note?: string }>("/database/drift"),
    retry: false,
  });
  const detail = useQuery({
    queryKey: ["db-table", open],
    enabled: !!open,
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/database/tables/${encodeURIComponent(String(open))}`),
    retry: false,
  });

  const cols: DataListColumn<TableRow>[] = [
    { key: "name", label: "Table", sortable: true, render: (t) => <span className="font-mono text-[12.5px]">{t.name}</span> },
    {
      key: "rows",
      label: "Rows",
      sortable: true,
      value: (t) => t.rows ?? -1,
      render: (t) =>
        t.rows == null ? (
          <span className="text-muted" title={t.rows_error}>
            not counted
          </span>
        ) : (
          t.rows.toLocaleString()
        ),
    },
    { key: "size_kb", label: "Size", sortable: true, value: (t) => t.size_kb, render: (t) => `${(t.size_kb / 1024).toFixed(1)} MB` },
  ];

  return (
    <div>
      {/* NOT "on this node". The ozpanel schema is Galera-replicated: measured on the pair,
          both nodes report the same 86 tables and the same row counts to the row. Saying "on
          this node" invites the reader to think a difference here would be local, and there is
          no such thing — anything written lands on every member. */}
      <PageHeader
        title="Platform database"
        subtitle="ozpanel's own records — the same on every node in the cluster"
      />
      {/* Read-only, deliberately, and the page has to SAY so. Without it the absence of any
          add/edit/delete reads as an unfinished screen rather than a decision, which is exactly
          how it read. These tables are the panel's own state and they are certified to every
          member: a hand-edited row is not a local fix, it is a cluster-wide one, applied with
          none of the validation, migration or reconciliation the code around it depends on.
          Changing what is here is done through the feature that owns it. */}
      <div className="mt-3 rounded-lg border border-line bg-bg2 px-3 py-2 text-[12.5px] leading-relaxed text-muted">
        Read-only, on purpose. This is ozpanel&apos;s own state, replicated to every node — an edit
        here would be certified across the whole cluster with none of the validation the owning
        feature applies. Change these values through the feature that owns them (a tenant on{" "}
        <span className="font-mono">Tenants</span>, a vhost on <span className="font-mono">Web</span>,
        and so on); this page is for reading what is actually stored and what has grown.
      </div>
      <Section title="Overview">
        <StateView loading={drift.isLoading} error={drift.error} errorContext="the schema overview">
          <div className="flex flex-wrap gap-6 text-[13px]">
            <Stat n={tables.data?.total_tables} label="tables" />
            <Stat n={tables.data?.total_rows ?? undefined} label="rows" />
            <Stat n={drift.data?.migrations_applied} label="migrations applied" />
            <Stat n={drift.data?.empty_tables?.length} label="empty tables" />
          </div>
          {/* An empty table is NOT evidence of a fault — the server says so itself, and repeating
              its note here stops the count reading as a problem to chase. */}
          {drift.data?.note && <div className="mt-3 max-w-3xl text-[12px] leading-relaxed text-muted">{drift.data.note}</div>}
        </StateView>
      </Section>

      <Section title="Tables" className="mt-6" bodyClassName="p-0">
        {tables.data?.note && <div className="border-b border-line px-4 py-2.5 text-[12px] text-muted">{tables.data.note}</div>}
        <div className="p-4">
          <DataList
            items={tables.data?.tables ?? []}
            columns={cols}
            getRowKey={(t) => t.name}
            loading={tables.isLoading}
            error={tables.error}
            errorContext="the table list"
            emptyMessage="No tables."
            rowActions={(t) => (
              <Button size="sm" variant="ghost" onClick={() => setOpen(open === t.name ? null : t.name)}>
                {open === t.name ? "Close" : "Columns"}
              </Button>
            )}
          />
        </div>
      </Section>

      {open && (
        <Section className="mt-6" title={open} actions={<Button size="sm" variant="ghost" onClick={() => setOpen(null)}>Close</Button>}>
          <StateView loading={detail.isLoading} error={detail.error} errorContext="this table">
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg border border-line bg-bg px-3 py-2 font-mono text-[11.5px]">
              {JSON.stringify(detail.data, null, 1)}
            </pre>
          </StateView>
        </Section>
      )}
    </div>
  );
}

function Stat({ n, label }: { n?: number; label: string }) {
  return (
    <div>
      <div className="text-[20px]">{n?.toLocaleString() ?? "—"}</div>
      <div className="text-[11.5px] text-muted">{label}</div>
    </div>
  );
}

// ─────────────────────────── support services ───────────────────────────

interface SupportService {
  name: string;
  role?: string;
  description?: string;
  installed?: boolean;
  running?: boolean;
  healthy?: boolean;
  needs_db?: boolean;
  opted_in?: number;
  /** SOGo only: hosted mail domains the operator switched off (mail domain → Settings → SOGo). */
  opted_out?: number;
  state?: string;
  shape?: string;
  /** What is ACTUALLY installed, asked of dpkg rather than of our own records. Empty when the
   *  service names no package or dpkg could not answer — an absent version means "not measured"
   *  and must not be drawn as a version. */
  version?: string;
  default_host?: string;
  default_path?: string;
}
interface DbAccount {
  service: string;
  base?: string;
  now?: string;
  per_node_name?: string;
  exists?: boolean;
  shared?: boolean;
  why?: string;
}

export function SupportServicesPanel() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [confirm, setConfirm] = React.useState<{ title: string; body: string; label: string; go: () => Promise<unknown> } | null>(null);
  const [detail, setDetail] = React.useState<string | null>(null);

  const one = useQuery({
    queryKey: ["support-service", detail],
    enabled: !!detail,
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/support-services/${encodeURIComponent(String(detail))}`),
    retry: false,
  });

  const svc = useQuery({
    queryKey: ["support-services"],
    queryFn: () => client.apiFetch<{ items: SupportService[] | null; note?: string }>("/support-services"),
    retry: false,
  });
  const accounts = useQuery({
    queryKey: ["support-db-accounts"],
    queryFn: () => client.apiFetch<{ accounts: DbAccount[] | null; shared_count?: number; note?: string }>("/support-services/db-accounts"),
    retry: false,
  });

  const run = (label: string, p: Promise<unknown>) =>
    p
      .then(() => {
        notify({ kind: "success", title: label });
        void qc.invalidateQueries({ queryKey: ["support-services"] });
        void qc.invalidateQueries({ queryKey: ["support-db-accounts"] });
      })
      .catch((e: unknown) => notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) }));

  return (
    <div>
      {svc.data?.note && <div className="mb-4 max-w-3xl text-[12px] leading-relaxed text-muted">{svc.data.note}</div>}

      <StateView loading={svc.isLoading} error={svc.error} errorContext="the support services">
        <div className="space-y-4">
          {(svc.data?.items ?? []).map((s) => {
            const acct = (accounts.data?.accounts ?? []).find((a) => a.service === s.name);
            return (
              <Section
                key={s.name}
                title={s.name}
                // The version beside the name, because "installed and running" says nothing about
                // WHICH version is serving. Nothing reported this for any service, which is how
                // Roundcube stayed a whole upstream branch behind while every indicator was green.
                hint={s.version || (s.installed ? "version not measured" : undefined)}
                actions={
                  <div className="flex flex-wrap gap-2">
                    {!s.installed ? (
                      <Button size="sm" onClick={() => void run(`${s.name} installed`, client.apiFetch(`/support-services/${encodeURIComponent(s.name)}/install`, { method: "POST" }))}>
                        Install
                      </Button>
                    ) : (
                      <>
                        {/* Install alone does NOT apply a config change — the reconcile is what
                            renders, diffs and restarts. A node once ran five days across three
                            "applied" fixes because install pre-wrote the config, so the reconcile
                            compared against its own write and skipped the restart. */}
                        <Button size="sm" onClick={() => void run(`${s.name} reconfigured`, client.apiFetch(`/support-services/${encodeURIComponent(s.name)}/reconfigure`, { method: "POST" }))}>
                          Reconfigure
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => void run(`${s.name} checked`, client.apiFetch(`/support-services/${encodeURIComponent(s.name)}/verify`))}>
                          Verify
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDetail(detail === s.name ? null : s.name)}>
                          {detail === s.name ? "Close" : "Details"}
                        </Button>
                      </>
                    )}
                  </div>
                }
              >
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill tone={s.installed ? "ok" : "neutral"}>{s.installed ? "installed" : "not installed"}</StatusPill>
                  {s.installed && <StatusPill tone={s.running ? "ok" : "bad"}>{s.running ? "running" : "stopped"}</StatusPill>}
                  {s.installed && <StatusPill tone={s.healthy ? "ok" : "warn"}>{s.healthy ? "healthy" : "unhealthy"}</StatusPill>}
                  {s.role && <span className="text-[11.5px] text-muted">{s.role}</span>}
                  {/* SOGo is every mail domain's by default (decision A9), so its count is not an opt-in. */}
                  {s.opted_in != null && (
                    <span className="text-[11.5px] text-muted">
                      {s.name === "sogo"
                        ? `${s.opted_in} mail domain(s)${s.opted_out ? `, ${s.opted_out} switched off` : " — every mail domain, no opt-in needed"}`
                        : `${s.opted_in} domain(s) opted in`}
                    </span>
                  )}
                </div>
                {s.description && <p className="mt-2 max-w-3xl text-[12px] leading-relaxed text-muted">{s.description}</p>}
                {/* The service's OWN assertion about itself, not our record of it. */}
                {s.state && <p className="mt-2 font-mono text-[11.5px] text-muted">{s.state}</p>}

                {detail === s.name && (
                  <div className="mt-3">
                    <StateView loading={one.isLoading} error={one.error} errorContext={`${s.name}'s detail`}>
                      <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-lg border border-line bg-bg px-3 py-2 font-mono text-[11px]">
                        {JSON.stringify(one.data, null, 1)}
                      </pre>
                    </StateView>
                  </div>
                )}

                {s.needs_db && acct && (
                  <div className="mt-3 rounded-lg border border-line p-3">
                    <div className="text-[12px]">
                      Database account <span className="font-mono">{acct.now || acct.base}</span>
                      {acct.exists === false && <StatusPill tone="bad">missing</StatusPill>}
                      {acct.shared && <StatusPill tone="warn">shared across nodes</StatusPill>}
                    </div>
                    {/* Galera replicates DCL, so a fixed account name with a per-node password is a
                        cluster bug: the second node to provision silently changes the first node's
                        credential. `why` is the server's own explanation of which case this is. */}
                    {acct.why && <div className="mt-1 max-w-3xl text-[11.5px] leading-relaxed text-muted">{acct.why}</div>}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {acct.shared && (
                        <Button size="sm"
                          onClick={() => setConfirm({
                            title: `Give ${s.name} its own database account on this node?`,
                            body: "A shared account name with a per-node password is a cluster bug — Galera replicates the grant, so the next node to provision silently changes this node's credential. Migrating gives this node an account no other node can overwrite.",
                            label: "Migrate",
                            go: () => client.apiFetch(`/support-services/${encodeURIComponent(s.name)}/db-account/migrate`, { method: "POST" }),
                          })}>
                          Give it its own account
                        </Button>
                      )}
                      <Button size="sm" variant="ghost"
                        onClick={() => setConfirm({
                          title: `Rotate ${s.name}'s database password?`,
                          body: "A new password is generated and written to the service's config, then the service is restarted. It is briefly unable to reach the database while that happens.",
                          label: "Rotate",
                          go: () => client.apiFetch(`/support-services/${encodeURIComponent(s.name)}/db-password/rotate`, { method: "POST" }),
                        })}>
                        Rotate password
                      </Button>
                    </div>
                  </div>
                )}
              </Section>
            );
          })}
        </div>
      </StateView>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title ?? ""}
        confirmLabel={confirm?.label ?? "Run"}
        body={<p>{confirm?.body}</p>}
        onConfirm={() => {
          const c = confirm;
          setConfirm(null);
          if (c) void run(c.label, c.go());
        }}
      />
    </div>
  );
}
